import { useCallback, useEffect, useState } from 'react';

import { brazilianRealFormat, createUrl, request } from 'app/utils';
import { Flex, Section, Typography } from 'components';
import { AvailableClassesApiResponse, ProductTypeEnum, ProductsSidebarButton, ProductsSidebarInterestButton, useProducts } from 'features/products';
import { useSchool } from 'features/school';
import { contentsTypes, useContents } from 'features/contents';
import { PagesTypes } from 'app/utils/createUrl';

interface EnrollmentProps {
  productSlug: string;
  productClassUuid: string;
  fecthApi: (forceEnroll: boolean) => void;
}

const Enrollment = ({ productSlug, productClassUuid, fecthApi }: EnrollmentProps) => {
  const [paymentLinkUrl, setPaymentLinkUrl] = useState('');

  const { productDetails, availableClasses, setAvailableClasses, clearAvailableClasses, productsContinuation } = useProducts();
  const { contentsList } = useContents();

  const { apiGatewayUrl, schoolAlias } = useSchool();

  const fetchPayment = useCallback(async () => {
    await Promise.all([
      request<AvailableClassesApiResponse>({
        url: `/api/v1/School/AvailableClasses/${productSlug}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then(({ data }) => {
        setAvailableClasses(data);
      }),

      productClassUuid
        ? request({
            url: `/api/v1/OfferPayment/Buy/${productClassUuid}`,
            method: 'get',
            baseURL: apiGatewayUrl,
          }).then(({ data }) => {
            if (typeof data.link === 'string') setPaymentLinkUrl(data.link || '');
          })
        : null,
    ]);
  }, [productSlug, productClassUuid]);

  useEffect(() => {
    fetchPayment();
  }, [productSlug, productClassUuid]);

  useEffect(() => {
    return () => {
      clearAvailableClasses();
    };
  }, []);

  let contentLink = '';
  let contentLabel = 'Assistir agora';
  let contentPageType: PagesTypes = 'productContentClassDetails';

  if (productDetails?.enrolled) {
    if (productsContinuation?.currentContentUuid || productsContinuation?.nextContentUuid) {
      if (!productsContinuation.firstContent) {
        contentLabel = 'Continuar';
      }

      if (
        productDetails?.productDetail.productType === ProductTypeEnum.MasterClass ||
        productDetails?.productDetail.productType === ProductTypeEnum.Journey ||
        productDetails?.productDetail.productType === ProductTypeEnum.Program
      ) {
        contentPageType = 'productNoClassContent';
      }
      //

      contentLink = productsContinuation.currentContentUuid
        ? createUrl(contentPageType, [
            productsContinuation.productSlug,
            productClassUuid,
            productsContinuation.currentContentProductClassTimelineUuid,
            productsContinuation.currentContentUuid,
            contentsTypes(productsContinuation.currentContentType, productsContinuation.currentExternalDocumentType),
          ])
        : createUrl(contentPageType, [
            productsContinuation.productSlug,
            productClassUuid,
            productsContinuation.nextContentProductClassTimelineUuid,
            productsContinuation.nextContentUuid,
            contentsTypes(productsContinuation.nextContentType, productsContinuation.nextExternalDocumentType),
          ]);
    } else {
      contentLink = createUrl(contentPageType, [
        productSlug,
        productClassUuid,
        '',
        contentsList[0]?.uuid,
        contentsTypes(contentsList[0]?.contentType, contentsList[0]?.externalDocumentType),
      ]);
    }
  }

  return (
    <Section flexGrow={0}>
      {productDetails?.enrolled ? (
        <Flex mt={4} maxWidth={{ d: 450 }}>
          <ProductsSidebarButton label={contentLabel} type="internal" to={contentLink} borderRadiusTop />
        </Flex>
      ) : (
        <>
          {schoolAlias === 'b3' ? (
            <>
              {availableClasses?.current?.allowEnrollments && (
                <>
                  {productDetails?.allowOnDemandEnrollments ? (
                    <Flex mt={4} maxWidth={{ m: ' 100%', d: 450 }} flexDirection="column">
                      <ProductsSidebarButton onClick={() => fecthApi(true)} label="Matricular agora" type="nolink" to="" borderRadiusTop />
                    </Flex>
                  ) : (
                    <Flex mt={4} maxWidth={{ m: ' 100%', d: 450 }} flexDirection="column">
                      <ProductsSidebarInterestButton label={`Tenho interesse nessa MasterClass`} labelMobile="Tenho interesse" bordered />
                    </Flex>
                  )}
                </>
              )}
            </>
          ) : availableClasses?.current?.allowEnrollments && !!paymentLinkUrl ? (
            <Flex flexDirection="column">
              {availableClasses.current.installmentValue ? (
                <Flex flexDirection="column" mt={4} maxWidth={{ d: 450 }}>
                  <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
                    <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                      Compre
                    </Typography>

                    <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2}>
                      {availableClasses.current.installment}x {brazilianRealFormat(availableClasses.current.installmentValue)}
                    </Typography>

                    <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                      ou {brazilianRealFormat(availableClasses.current.price)} a vista
                    </Typography>
                  </Flex>

                  <ProductsSidebarButton label="Comprar agora" type="external" to={paymentLinkUrl} target="_blank" />
                </Flex>
              ) : (
                <Flex flexDirection="column" mt={4} maxWidth={{ d: 450 }}>
                  <ProductsSidebarButton label="Matricular agora" type="external" to={paymentLinkUrl} target="_blank" borderRadiusTop />
                </Flex>
              )}
            </Flex>
          ) : (
            <Flex flexDirection="column" mt={4} maxWidth={{ d: 450 }}>
              <Flex
                p={{ m: 2, d: 4 }}
                flexDirection="column"
                bg={schoolAlias === 'b3' ? 'dark_350' : 'dark_350_70'}
                borderTopLeftRadius={4}
                borderTopRightRadius={4}
              >
                <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                  Inscrição
                </Typography>

                <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2} upper>
                  Matrícula não disponível
                </Typography>

                <Typography as="span" type="type2" lineHeight="1.3" color="white" mt={2}>
                  Demonstre seu interesse nesse produto.
                </Typography>
              </Flex>

              <ProductsSidebarInterestButton label={`Tenho interesse nesse produto`} />
            </Flex>
          )}
        </>
      )}
    </Section>
  );
};

export default Enrollment;
